import React from "react";
import Seo from "@/components/Seo";
import PhotoBackdrop from "@/components/PhotoBackdrop";
import Breadcrumb from "@/components/Breadcrumb";
import CTABanner from "@/components/CTABanner";
import FAQAccordion from "@/components/FAQAccordion";
import RelatedBlock from "@/components/RelatedBlock";
import ArticleCard from "@/components/cards/ArticleCard";
import ServiceCard from "@/components/cards/ServiceCard";
import { Helmet } from "react-helmet-async";
import { ALL_SERVICES } from "@/data/nav";

/**
 * ArticleTemplate — shared shell for /insights/:slug pages.
 *
 * Props (article object):
 *  - slug, title, dek, category, readTime, publishedAt, image, imagePosition
 *  - body: React node (main long-form content, wrapped in prose styling)
 *  - faqs: [{ q, a }]
 *  - relatedServiceSlugs: string[]
 *  - relatedArticles: [{ slug, title, dek, category, readTime, publishedAt, image }]
 */
const ArticleTemplate = ({ article }) => {
  const {
    slug,
    title,
    dek,
    category,
    readTime,
    publishedAt,
    updatedAt,
    image,
    imagePosition = "center 40%",
    body,
    faqs = [],
    relatedServiceSlugs = [],
    relatedArticles = [],
  } = article;

  const publishedISO = publishedAt ? new Date(publishedAt).toISOString() : null;
  const updatedISO = updatedAt ? new Date(updatedAt).toISOString() : publishedISO;
  const dateStr = publishedAt
    ? new Date(publishedAt).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  // Article JSON-LD (schema.org Article) — factual only, no fake ratings.
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: dek,
    ...(publishedISO ? { datePublished: publishedISO } : {}),
    ...(updatedISO ? { dateModified: updatedISO } : {}),
    author: { "@type": "Organization", name: "Lutrom Engineering" },
    publisher: {
      "@type": "Organization",
      name: "Lutrom Engineering",
      logo: {
        "@type": "ImageObject",
        url: "https://www.lutrom.co.uk/brand/lutrom-mark.png",
      },
    },
    mainEntityOfPage: `https://www.lutrom.co.uk/insights/${slug}`,
  };

  const relatedServices = relatedServiceSlugs
    .map((s) => ALL_SERVICES.find((x) => x.slug === s))
    .filter(Boolean)
    .map((s) => (
      <ServiceCard
        key={s.slug}
        slug={s.slug}
        name={s.name}
        blurb={s.blurb}
        categoryName={s.categoryName}
      />
    ));

  const relatedArticleItems = relatedArticles.map((a) => (
    <ArticleCard
      key={a.slug}
      slug={a.slug}
      title={a.title}
      dek={a.dek}
      category={a.category}
      readTime={a.readTime}
      publishedAt={a.publishedAt}
    />
  ));

  return (
    <article data-testid={`insights-article-${slug}`}>
      <Seo title={title} description={dek} path={`/insights/${slug}`} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>

      {/* -------- Hero -------- */}
      <section className="relative overflow-hidden">
        {image && <PhotoBackdrop src={image} position={imagePosition} />}
        <div className="max-w-4xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-14 lg:pb-20 relative">
          <p className="eyebrow" data-testid="article-eyebrow">
            {category ? `Insights · ${category}` : "Insights"}
          </p>
          <h1
            className="font-display mt-6 text-white"
            style={{ fontSize: "clamp(36px, 5vw, 68px)", lineHeight: 1.02 }}
            data-testid="article-heading"
          >
            {title}
          </h1>
          {dek && (
            <p className="mt-6 text-[17px] leading-relaxed text-[color:var(--lt-text-dim)] max-w-3xl">
              {dek}
            </p>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-3 text-[12.5px] text-[color:var(--lt-text-mute)]">
            {dateStr && (
              <span data-testid="article-date">
                Published <time dateTime={publishedISO}>{dateStr}</time>
              </span>
            )}
            {readTime && (
              <>
                <span aria-hidden="true">·</span>
                <span data-testid="article-readtime">{readTime}</span>
              </>
            )}
            <span aria-hidden="true">·</span>
            <span>By Lutrom Engineering</span>
          </div>
        </div>
      </section>

      <Breadcrumb items={[{ label: "Insights", to: "/insights" }, { label: title }]} />

      {/* -------- Body -------- */}
      <section className="max-w-3xl mx-auto px-5 lg:px-10 mt-12 lg:mt-16">
        <div
          className="article-prose text-[16px] leading-[1.75] text-[color:var(--lt-text-dim)]"
          data-testid="article-body"
        >
          {body}
        </div>
      </section>

      {/* -------- FAQs -------- */}
      {faqs.length > 0 && (
        <section
          className="max-w-3xl mx-auto px-5 lg:px-10 mt-20"
          data-testid="article-faq-section"
        >
          <p className="eyebrow">Frequently asked</p>
          <h2
            className="font-display mt-4 text-white"
            style={{ fontSize: "clamp(28px, 3.4vw, 40px)", lineHeight: 1 }}
          >
            QUESTIONS WE OFTEN GET.
          </h2>
          <div className="mt-8">
            <FAQAccordion items={faqs} testIdPrefix={`faq-${slug}`} />
          </div>
        </section>
      )}

      {/* -------- CTA -------- */}
      <CTABanner
        variant="standard"
        title="Want us to look at your site?"
        body="Talk to a working engineer — not a call centre. Every enquiry gets a same-day human reply."
        phoneLocation={`insights_article_${slug}`}
      />

      {/* -------- Related -------- */}
      <RelatedBlock
        title="Read next & work with us"
        groups={[
          { eyebrow: "Related services", items: relatedServices },
          { eyebrow: "More insights", items: relatedArticleItems },
        ]}
      />
    </article>
  );
};

export default ArticleTemplate;
